import JSZip from "jszip";
import type { OperationFactory, Range } from "@otrack/domain";
import { currencyDigits, currencySchema, type Currency, type SyncOperation } from "@otrack/domain";
import { parseCsv } from "./csv";

type Row = Record<string, string>;

/**
 * Reads solidtime's organization export (`export.zip`). See ADR 0006 for the mapping. Imported ids
 * are derived from solidtime ids, so importing the same export twice updates instead of duplicating.
 */
export interface SolidtimeExport {
  organization: string;
  currency: Currency;
  organizationRate: number | null;
  /** Start of the earliest and latest time entry, or null when the export has none. */
  span: Range | null;
  clients: Row[];
  projects: Row[];
  tasks: Row[];
  entries: Row[];
}

export interface SolidtimeImport {
  organization: string;
  currency: Currency;
  counts: { clients: number; projects: number; tasks: number; timeEntries: number };
  operations: SyncOperation[];
}

const timestamp = (value: string | undefined): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** solidtime stores every rate in cents (hundredths), whatever the currency. */
function centsToMinor(value: string | undefined, currency: Currency): number | null {
  if (value === undefined || value === "") return null;
  const cents = Number(value);
  if (!Number.isFinite(cents) || cents < 0) return null;
  return Math.round(cents / 100 * 10 ** currencyDigits(currency));
}

async function readCsv(zip: JSZip, name: string, required: boolean): Promise<Row[]> {
  const file = zip.file(name);
  if (!file) {
    if (required) throw new Error(`This does not look like a solidtime export: ${name} is missing.`);
    return [];
  }
  return parseCsv(await file.async("string"));
}

export async function readSolidtimeExport(data: Blob | ArrayBuffer | Uint8Array): Promise<SolidtimeExport> {
  const zip = await JSZip.loadAsync(data);
  const [organizations, clients, projects, tasks, entries] = await Promise.all([
    readCsv(zip, "organizations.csv", true),
    readCsv(zip, "clients.csv", false),
    readCsv(zip, "projects.csv", true),
    readCsv(zip, "tasks.csv", false),
    readCsv(zip, "time_entries.csv", true)
  ]);
  const organization = organizations[0];
  if (!organization) throw new Error("The export has no organization.");
  const parsedCurrency = currencySchema.safeParse((organization["currency"] ?? "").toUpperCase());
  const currency: Currency = parsedCurrency.success ? parsedCurrency.data : "USD";
  let span: Range | null = null;
  for (const row of entries) {
    const start = timestamp(row["start"]);
    if (start === null) continue;
    span = span ? { from: Math.min(span.from, start), to: Math.max(span.to, start) } : { from: start, to: start };
  }
  return {
    organization: organization["name"] ?? "solidtime",
    currency,
    organizationRate: centsToMinor(organization["billable_rate"], currency),
    span,
    clients,
    projects,
    tasks,
    entries
  };
}

/**
 * Turns a read export into operations. `range` keeps only time entries that start inside it;
 * clients, projects, and tasks always come over so later imports of other periods land in place.
 */
export function planSolidtimeImport(source: SolidtimeExport, factory: OperationFactory, range: Range | null = null): SolidtimeImport {
  const { clients, projects, tasks, entries, currency, organizationRate } = source;
  const operations: SyncOperation[] = [];
  const projectIds = new Set<string>();
  const taskIds = new Set<string>();

  for (const row of clients) {
    if (!row["id"] || !row["name"]) continue;
    operations.push(factory.op("client", `client_st_${row["id"]}`, {
      name: row["name"].slice(0, 200),
      defaultCurrency: currency,
      archivedAt: timestamp(row["archived_at"])
    }));
  }
  for (const row of projects) {
    if (!row["id"] || !row["name"]) continue;
    projectIds.add(row["id"]);
    // Newer exports add billing_type and fixed_price; older ones only have is_billable.
    const billable = row["is_billable"] === "true";
    const fixed = billable && row["billing_type"] === "fixed";
    operations.push(factory.op("project", `project_st_${row["id"]}`, {
      name: row["name"].slice(0, 200),
      clientId: row["client_id"] ? `client_st_${row["client_id"]}` : null,
      billingMode: !billable ? "non-billable" : fixed ? "fixed" : "hourly",
      currency,
      hourlyRate: billable && !fixed ? centsToMinor(row["billable_rate"], currency) ?? organizationRate : null,
      fixedPrice: fixed ? centsToMinor(row["fixed_price"], currency) : null,
      archivedAt: timestamp(row["archived_at"])
    }));
  }
  for (const row of tasks) {
    if (!row["id"] || !row["name"] || !row["project_id"] || !projectIds.has(row["project_id"])) continue;
    taskIds.add(row["id"]);
    operations.push(factory.op("task", `task_st_${row["id"]}`, {
      projectId: `project_st_${row["project_id"]}`,
      name: row["name"].slice(0, 200),
      doneAt: timestamp(row["done_at"])
    }));
  }
  let timeEntries = 0;
  for (const row of entries) {
    const start = timestamp(row["start"]);
    if (!row["id"] || start === null) continue;
    if (range && (start < range.from || start >= range.to)) continue;
    const end = timestamp(row["end"]);
    if (end !== null && end < start) continue;
    const projectId = row["project_id"] && projectIds.has(row["project_id"]) ? `project_st_${row["project_id"]}` : null;
    const billable = row["billable"] === "true" && projectId !== null;
    const rate = billable ? centsToMinor(row["billable_rate"], currency) : null;
    operations.push(factory.op("timeEntry", `entry_st_${row["id"]}`, {
      projectId,
      taskId: row["task_id"] && taskIds.has(row["task_id"]) ? `task_st_${row["task_id"]}` : null,
      description: (row["description"] ?? "").slice(0, 1000),
      start,
      end,
      billable,
      rateSnapshot: rate === null ? null : { amount: rate, currency },
      invoiceId: null
    }));
    timeEntries += 1;
  }
  return {
    organization: source.organization,
    currency,
    counts: { clients: clients.length, projects: projectIds.size, tasks: taskIds.size, timeEntries },
    operations
  };
}
